import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { PassportPublicationBlockers, PassportQrTokenMeta } from '@siliconx/shared';
import { Button } from '@siliconx/shared/ui/Button';
import { Card } from '@siliconx/shared/ui/Card';
import { PanelTitle } from '@siliconx/shared/ui/PanelTitle';
import { api, throwIfNotOk } from '../../lib/api';
import { useAuth } from '../../lib/authContext';
import { formatDate } from '../../lib/format';
import { qk } from '../../lib/queryKeys';
import {
  passportErrorMessage,
  passportToggleHidden,
  setPassportPublished,
  usePassportPublication,
  usePassportQrTokens,
} from '../../lib/passportPublication';
import { useModalTrap } from '../../routes/assets/useModalTrap';

/**
 * SIL-2409 (PR 5): the owner controls of the public buyer passport on the asset page.
 *
 *  - "Public passport" toggle: org admins change it, members see the state read-only, a takedown by operations locks it
 *    with copy, and sold / disposed assets show nothing (the sale / disposal hook already made the passport private).
 *  - The share-link list: every QR token of the asset (bench labels and owner share links), admin revoke, and the
 *    `issuedBeforeTransfer` flag for labels the previous owner printed.
 *  - Publishing a clone that still holds active pre-transfer labels offers to revoke them first (stating the reprint cost).
 *
 * The server is the authority for every gate here (the definer functions re-check admin and takedown); this card only
 * reflects what the API says. Copy never claims the passport is verified or certified: it states what becomes visible.
 */

export const PUBLIC_PASSPORT_VISIBLE_COPY =
  'Anyone with a QR link to this asset can see its redacted history ledger and the components installed in it. ' +
  'The owner name is never shown.';

/** SIL-2439: a component published on its own. The system passport is separate and is never public at the same time. */
export const PUBLIC_COMPONENT_PASSPORT_VISIBLE_COPY =
  'Anyone with a QR link to this component can see its redacted history ledger. The owner name is never shown. ' +
  'The system passport is separate and cannot be public at the same time.';

/** SIL-2439: why a component cannot be published right now (the system is public or taken down). */
export const COMPONENT_PARENT_PUBLIC_COPY =
  'The system passport is marked public, so this component cannot be published on its own. ' +
  'Make the system passport private first.';
export const COMPONENT_PARENT_TAKEN_DOWN_COPY =
  'The system this component is installed in was taken down by operations, so this component stays private too.';

/** Active (not revoked) tokens the previous owner issued before a sale forwarded them to this asset. */
export function activePreTransferTokens(tokens: readonly PassportQrTokenMeta[]): PassportQrTokenMeta[] {
  return tokens.filter((t) => t.issuedBeforeTransfer && t.revokedAt == null);
}

const SOURCE_LABEL: Record<PassportQrTokenMeta['source'], string> = {
  bench_label: 'Bench label',
  owner_share: 'Share link',
};

export function PublicPassportCard({
  assetId,
  lifecycleStateId,
  isComponent = false,
}: {
  assetId: string;
  lifecycleStateId: string | null;
  /** Selects the component copy; the server is the authority for the system / component exclusivity. */
  isComponent?: boolean;
}) {
  // SIL-2439: components are publishable on their own (the server refuses while the system is public); only sold / disposed hide the card
  const hidden = passportToggleHidden(lifecycleStateId);
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const pub = usePassportPublication(assetId, !hidden);
  const tokensQ = usePassportQrTokens(assetId, !hidden);
  const [dialog, setDialog] = useState<'publish' | 'unpublish' | null>(null);

  if (hidden) return null;
  if (pub.isLoading) return null;
  if (pub.isError || !pub.data) return null;

  const state = pub.data;
  const tokens = tokensQ.data?.tokens ?? [];
  const locked = state.takenDown;
  const blockers = state.blockers;
  const parentPublic = isComponent && !state.published && blockers?.parentPublished === true;
  const parentDown = isComponent && !state.published && blockers?.parentTakenDown === true;
  const canToggle = isAdmin && !locked && !parentPublic && !parentDown;
  const visibleCopy = isComponent ? PUBLIC_COMPONENT_PASSPORT_VISIBLE_COPY : PUBLIC_PASSPORT_VISIBLE_COPY;

  return (
    <Card className="scroll-mt-6" id="public-passport" data-testid="public-passport-card">
      <PanelTitle
        title="Public passport"
        description={state.published ? 'On - anyone with a QR link can view it.' : 'Off - only your organization can view it.'}
        right={
          <button
            type="button"
            role="switch"
            aria-checked={state.published}
            aria-label="Public passport"
            disabled={!canToggle}
            onClick={() => setDialog(state.published ? 'unpublish' : 'publish')}
            data-testid="public-passport-toggle"
            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
              state.published ? 'bg-brand-600' : 'bg-muted-foreground/40'
            }`}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                state.published ? 'translate-x-5' : 'translate-x-0.5'
              }`}
            />
          </button>
        }
      />
      <p className="mt-3 text-sm text-muted-foreground">{visibleCopy}</p>
      {locked && (
        <p role="status" className="mt-2 text-sm text-amber-700" data-testid="public-passport-takedown">
          This passport was taken down by operations. The toggle is locked until operations restores it.
        </p>
      )}
      {!locked && parentDown && (
        <p role="status" className="mt-2 text-sm text-amber-700" data-testid="public-passport-parent-down">
          {COMPONENT_PARENT_TAKEN_DOWN_COPY}
        </p>
      )}
      {!locked && !parentDown && parentPublic && (
        <p role="status" className="mt-2 text-sm text-amber-700" data-testid="public-passport-parent-public">
          {COMPONENT_PARENT_PUBLIC_COPY}
        </p>
      )}
      {state.published && tokens.filter((t) => !t.revokedAt).length === 0 && !tokensQ.isLoading && !tokensQ.isError && (
        <p className="mt-2 text-xs text-muted-foreground" data-testid="public-passport-no-link">
          Marked public, but there is no active QR link yet. Create a share link so people can open it.
        </p>
      )}
      {!locked && !parentDown && !parentPublic && !isAdmin && (
        <p className="mt-2 text-xs text-muted-foreground" data-testid="public-passport-readonly">
          Only an organization admin can change this.
        </p>
      )}

      <ShareLinks assetId={assetId} tokens={tokens} isLoading={tokensQ.isLoading} isError={tokensQ.isError} isAdmin={isAdmin} />

      {dialog && (
        <PublishDialog
          assetId={assetId}
          mode={dialog}
          isComponent={isComponent}
          preTransfer={activePreTransferTokens(tokens)}
          blockingComponents={blockers?.publishedComponents ?? []}
          onClose={() => setDialog(null)}
        />
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Publish / unpublish dialog
// ---------------------------------------------------------------------------

function PublishDialog({
  assetId,
  mode,
  isComponent,
  preTransfer,
  blockingComponents,
  onClose,
}: {
  assetId: string;
  mode: 'publish' | 'unpublish';
  isComponent: boolean;
  preTransfer: PassportQrTokenMeta[];
  /** SIL-2439: components of this system that are public on their own (publishing the system is refused while any exist). */
  blockingComponents: PassportPublicationBlockers['publishedComponents'];
  onClose: () => void;
}) {
  const panelRef = useModalTrap(true);
  const queryClient = useQueryClient();
  // Safest default: retire labels the previous owner printed before the passport goes public under the new owner.
  const [revokePre, setRevokePre] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const offerRevoke = mode === 'publish' && preTransfer.length > 0;
  const blocked = mode === 'publish' && !isComponent && blockingComponents.length > 0;

  const mutation = useMutation({
    mutationFn: async () => {
      // Revoke first: the pre-transfer labels must never resolve while the new owner's passport is public.
      if (offerRevoke && revokePre) {
        for (const t of preTransfer) {
          const res = await api.api.client.passport[':assetId']['qr-tokens'][':tokenId'].$delete({
            param: { assetId, tokenId: t.id },
          });
          // 404 = already revoked (or gone): nothing left to revoke.
          if (!res.ok && res.status !== 404) await throwIfNotOk(res);
        }
      }
      return setPassportPublished(assetId, mode === 'publish', 'owner_toggle');
    },
    onSuccess: async () => {
      toast.success(mode === 'publish' ? 'Passport is now public.' : 'Passport is now private.');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: qk.assets.passportPublication(assetId) }),
        queryClient.invalidateQueries({ queryKey: qk.assets.passportQrTokens(assetId) }),
      ]);
      onClose();
    },
    onError: (err: Error) => {
      setError(passportErrorMessage(err));
      void queryClient.invalidateQueries({ queryKey: qk.assets.passportQrTokens(assetId) });
      // SIL-2439: the blocker list can be stale (another admin published a part); re-read it so the notice and the disabled state are current
      void queryClient.invalidateQueries({ queryKey: qk.assets.passportPublication(assetId) });
    },
  });

  return createPortal(
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/40"
      onKeyDown={(e) => {
        if (e.key === 'Escape' && !mutation.isPending) onClose();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="public-passport-dialog-title"
        className="w-full max-w-md rounded-xl bg-card p-6 shadow-xl"
      >
        <h2 id="public-passport-dialog-title" className="mb-2 text-base font-semibold text-foreground">
          {mode === 'publish' ? 'Make this passport public?' : 'Make this passport private?'}
        </h2>
        {mode === 'publish' ? (
          <p className="mb-3 text-sm text-muted-foreground">
            {isComponent ? PUBLIC_COMPONENT_PASSPORT_VISIBLE_COPY : PUBLIC_PASSPORT_VISIBLE_COPY} It stays public until you turn it off.
          </p>
        ) : (
          <p className="mb-3 text-sm text-muted-foreground">
            QR links to this asset will stop showing its passport. Your own organization can still view it. You can turn it on
            again later.
          </p>
        )}
        {blocked && (
          <div role="alert" className="mb-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="blocking-components">
            <p>These components are public on their own. Make them private first, then publish the system.</p>
            <ul className="mt-2 list-disc pl-5">
              {blockingComponents.map((c) => (
                <li key={c.assetId}>
                  <a href={`/assets/${c.assetId}`} className="font-medium underline" data-testid="blocking-component-link">
                    {c.modelName ?? c.assetClassId}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
        {offerRevoke && (
          <label className="mb-3 flex items-start gap-2 text-sm text-foreground" data-testid="revoke-pre-transfer">
            <input
              type="checkbox"
              checked={revokePre}
              onChange={(e) => setRevokePre(e.target.checked)}
              disabled={mutation.isPending}
              className="mt-0.5"
            />
            <span>
              Revoke {preTransfer.length} pre-transfer {preTransfer.length === 1 ? 'label' : 'labels'}. The previous owner printed
              {preTransfer.length === 1 ? ' it' : ' them'}. Revoked labels stop working and must be reprinted to be used again.
            </span>
          </label>
        )}
        {error && (
          <p role="alert" className="mb-3 text-xs text-red-600">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={mutation.isPending}
            className="rounded-md px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-muted disabled:opacity-50"
          >
            Cancel
          </button>
          <Button onClick={() => mutation.mutate()} disabled={mutation.isPending || blocked} data-testid="public-passport-confirm">
            {mutation.isPending ? 'Saving...' : mode === 'publish' ? 'Make public' : 'Make private'}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// Share-link (QR token) list
// ---------------------------------------------------------------------------

function ShareLinks({
  assetId,
  tokens,
  isLoading,
  isError,
  isAdmin,
}: {
  assetId: string;
  tokens: PassportQrTokenMeta[];
  isLoading: boolean;
  isError: boolean;
  isAdmin: boolean;
}) {
  const queryClient = useQueryClient();
  // The raw URL is returned once by the API and lives in component state only.
  const [issuedUrl, setIssuedUrl] = useState<string | null>(null);

  const refresh = () => queryClient.invalidateQueries({ queryKey: qk.assets.passportQrTokens(assetId) });

  const issue = useMutation({
    mutationFn: async () => {
      const res = await api.api.client.passport[':assetId']['qr-tokens'].$post({ param: { assetId } });
      await throwIfNotOk(res);
      return (await res.json()) as unknown as { url: string };
    },
    onSuccess: async (data) => {
      setIssuedUrl(data.url);
      await refresh();
    },
    onError: (err: Error) => toast.error(passportErrorMessage(err)),
  });

  const revoke = useMutation({
    mutationFn: async (tokenId: string) => {
      const res = await api.api.client.passport[':assetId']['qr-tokens'][':tokenId'].$delete({ param: { assetId, tokenId } });
      await throwIfNotOk(res);
    },
    onSuccess: async () => {
      toast.success('Link revoked.');
      await refresh();
    },
    onError: (err: Error) => {
      toast.error(passportErrorMessage(err));
      void refresh();
    },
  });

  return (
    <div className="mt-5 border-t border-border pt-4" data-testid="public-passport-tokens">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-foreground">QR links</h3>
        {isAdmin && (
          <Button variant="secondary" size="sm" onClick={() => issue.mutate()} disabled={issue.isPending}>
            {issue.isPending ? 'Creating...' : 'New share link'}
          </Button>
        )}
      </div>
      {issuedUrl && (
        <div className="mt-3 rounded-md border border-border bg-muted p-3" data-testid="issued-url">
          <p className="text-xs text-muted-foreground">Copy this link now. It is shown only once.</p>
          <p className="mt-1 break-all font-mono text-xs text-foreground">{issuedUrl}</p>
          <div className="mt-2 flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                void navigator.clipboard?.writeText(issuedUrl).then(() => toast.success('Link copied.'));
              }}
            >
              Copy
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setIssuedUrl(null)}>
              Done
            </Button>
          </div>
        </div>
      )}
      {isLoading ? (
        <p className="mt-3 text-sm text-muted-foreground">Loading...</p>
      ) : isError ? (
        <p className="mt-3 text-sm text-muted-foreground">Could not load QR links.</p>
      ) : tokens.length === 0 ? (
        <p className="mt-3 text-sm text-muted-foreground">No QR links for this asset yet.</p>
      ) : (
        <ul className="mt-3 divide-y divide-border">
          {tokens.map((t) => (
            <li key={t.id} className="flex flex-wrap items-center justify-between gap-2 py-2" data-testid="qr-token-row">
              <div className="min-w-0 text-sm">
                <span className={t.revokedAt ? 'text-muted-foreground line-through' : 'text-foreground'}>
                  {SOURCE_LABEL[t.source]}
                </span>
                <span className="ml-2 text-xs text-muted-foreground">Created {formatDate(t.createdAt)}</span>
                {t.issuedBeforeTransfer && (
                  <span
                    className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800"
                    data-testid="issued-before-transfer"
                  >
                    Issued before transfer
                  </span>
                )}
                {t.revokedAt && <span className="ml-2 text-xs text-muted-foreground">Revoked {formatDate(t.revokedAt)}</span>}
              </div>
              {isAdmin && !t.revokedAt && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => revoke.mutate(t.id)}
                  disabled={revoke.isPending}
                  aria-label={`Revoke ${SOURCE_LABEL[t.source].toLowerCase()} created ${formatDate(t.createdAt)}`}
                >
                  Revoke
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
